import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { PenLine, Gift, ScanLine, ArrowRight, Sparkles } from "lucide-react";

const STEP_ICONS = [PenLine, Gift, ScanLine];

/**
 * Lightweight, optional 3-step visual explainer for the Make It Personal journey.
 * - No heavy media (no video/MP4). Pure SVG icons + subtle CSS/framer transitions.
 * - Respects prefers-reduced-motion (renders a clean static version).
 * - Reusable: pass `heading` and `steps` so future placements share this component.
 */
export const PersonalizationExplainer = ({ heading, steps = [], className = "", testId = "personalization-explainer" }) => {
  const reduce = useReducedMotion();
  const visible = steps.slice(0, 3);

  const container = reduce
    ? {}
    : { hidden: {}, show: { transition: { staggerChildren: 0.12 } } };
  const item = reduce
    ? {}
    : {
        hidden: { opacity: 0, y: 10 },
        show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: "easeOut" } },
      };

  return (
    <section
      data-testid={testId}
      className={`rounded-wp-xl border border-wp-mint/40 bg-gradient-to-br from-wp-cream-100 to-wp-mint/10 p-5 sm:p-6 ${className}`}
    >
      {heading && (
        <div className="mb-4 flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-wp-gold" />
          <h3 className="font-display text-lg text-wp-ink sm:text-xl" data-testid="personalization-explainer-heading">{heading}</h3>
        </div>
      )}
      <motion.ol
        className="grid gap-4 sm:grid-cols-3 sm:gap-2"
        variants={container}
        initial={reduce ? false : "hidden"}
        whileInView={reduce ? undefined : "show"}
        viewport={{ once: true, amount: 0.3 }}
      >
        {visible.map((step, i) => {
          const Icon = STEP_ICONS[i] || PenLine;
          const isLast = i === visible.length - 1;
          return (
            <motion.li key={i} variants={item} className="relative flex items-start gap-3 sm:flex-col sm:items-center sm:text-center" data-testid={`personalization-explainer-step-${i + 1}`}>
              <span className="relative grid h-11 w-11 flex-shrink-0 place-items-center rounded-full bg-card text-wp-rose-strong shadow-wp-soft ring-1 ring-wp-mint/40">
                <Icon className="h-5 w-5" />
                {isLast && !reduce && (
                  <motion.span
                    className="absolute -right-0.5 -top-0.5 text-wp-gold"
                    animate={{ opacity: [0.4, 1, 0.4], scale: [0.9, 1.05, 0.9] }}
                    transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                    aria-hidden="true"
                  >
                    <Sparkles className="h-3.5 w-3.5" />
                  </motion.span>
                )}
              </span>
              <div className="sm:mt-2">
                <p className="text-sm font-semibold text-wp-ink">
                  <span className="mr-1 text-wp-rose-strong">{i + 1}.</span>{step.title}
                </p>
                <p className="mt-0.5 text-xs leading-relaxed text-wp-ink-muted">{step.copy}</p>
              </div>
              {/* Connector arrow between steps (desktop only) */}
              {!isLast && (
                <ArrowRight className="absolute -right-2 top-2.5 hidden h-4 w-4 text-wp-mint sm:block" aria-hidden="true" />
              )}
            </motion.li>
          );
        })}
      </motion.ol>
    </section>
  );
};

export default PersonalizationExplainer;
